import React from 'react';
import clsx from 'clsx';
import Box from '@mui/material/Box';
import MarkdownText from '@erikdakoda/markdown-ui/components/MarkdownText';
import SandboxedFrame from '@erikdakoda/markdown-ui/components/SandboxedFrame';
import MermaidArtifact from '@erikdakoda/markdown-ui/components/MermaidArtifact';
import { wrapInHtmlDoc } from '@erikdakoda/markdown-ui/shared/wrapInHtmlDoc';
import { registerArtifactType, ARTIFACT_PREVIEW_MAX_CHARS } from '../shared/artifactRegistry';
import type { ArtifactComponentProps } from '../shared/ArtifactTypeDefinition';
import SelfScrollingArtifactFrame from '../components/SelfScrollingArtifactFrame';

function defaultPreviewText(content: string): string {
  return content.slice(0, ARTIFACT_PREVIEW_MAX_CHARS).replace(/\n/g, ' ');
}

function defaultShouldTruncatePreview(content: string): boolean {
  return content.length > ARTIFACT_PREVIEW_MAX_CHARS;
}

function MarkdownArtifactComponent({
  content,
  artifactDepth = 0,
  onOpenArtifact,
  className,
  fillPanel = false,
}: ArtifactComponentProps): React.ReactElement {
  'use memo';

  const markdown = (
    <Box sx={{ p: 2 }}>
      <MarkdownText
        content={content}
        artifactDepth={artifactDepth + 1}
        onOpenArtifact={onOpenArtifact}
      />
    </Box>
  );

  if (!fillPanel) {
    return (
      <Box
        className={clsx('markdown-artifact-component', className)}
        sx={{ minWidth: 0, maxWidth: '100%' }}
      >
        {markdown}
      </Box>
    );
  }

  return (
    <SelfScrollingArtifactFrame className={clsx('markdown-artifact-component', className)}>
      {markdown}
    </SelfScrollingArtifactFrame>
  );
}

function HtmlArtifactComponent({
  content,
  title,
  className,
  fillPanel = false,
}: ArtifactComponentProps): React.ReactElement {
  'use memo';

  if (!fillPanel) {
    return (
      <SandboxedFrame
        className={clsx('html-artifact-component', className)}
        srcdoc={content}
        title={title}
      />
    );
  }

  return (
    <SelfScrollingArtifactFrame
      className={clsx('html-artifact-component', className)}
      useScrollBox={false}
    >
      <SandboxedFrame srcdoc={content} title={title} />
    </SelfScrollingArtifactFrame>
  );
}

function SvgArtifactComponent({
  content,
  title,
  className,
  fillPanel = false,
}: ArtifactComponentProps): React.ReactElement {
  'use memo';

  const srcdoc = wrapInHtmlDoc(content);

  if (!fillPanel) {
    return (
      <SandboxedFrame
        className={clsx('svg-artifact-component', className)}
        srcdoc={srcdoc}
        title={title}
      />
    );
  }

  return (
    <SelfScrollingArtifactFrame
      className={clsx('svg-artifact-component', className)}
      useScrollBox={false}
    >
      <SandboxedFrame srcdoc={srcdoc} title={title} />
    </SelfScrollingArtifactFrame>
  );
}

function MermaidArtifactComponent({
  content,
  title,
  className,
  fillPanel = false,
}: ArtifactComponentProps): React.ReactElement {
  'use memo';

  if (!fillPanel) {
    return (
      <MermaidArtifact
        className={clsx('mermaid-artifact-component', className)}
        content={content}
        title={title}
      />
    );
  }

  return (
    <SelfScrollingArtifactFrame
      className={clsx('mermaid-artifact-component', className)}
      useScrollBox={false}
    >
      <MermaidArtifact content={content} title={title} />
    </SelfScrollingArtifactFrame>
  );
}

/** Registers core artifact types that wrap markdown-ui primitives. */
export function registerCoreArtifactTypes(): void {
  registerArtifactType({
    type: 'text/markdown',
    label: 'Markdown',
    name: 'Markdown Text',
    icon: 'thin-markdown-file',
    fileExtension: '.md',
    isPointer: false,
    getPreviewText: defaultPreviewText,
    shouldTruncatePreview: defaultShouldTruncatePreview,
    Component: MarkdownArtifactComponent,
  });

  registerArtifactType({
    type: 'text/html',
    label: 'HTML',
    name: 'HTML Document',
    icon: 'file-html',
    fileExtension: '.html',
    isPointer: false,
    getPreviewText: defaultPreviewText,
    shouldTruncatePreview: defaultShouldTruncatePreview,
    Component: HtmlArtifactComponent,
  });

  registerArtifactType({
    type: 'image/svg+xml',
    label: 'SVG',
    name: 'SVG Image',
    icon: 'draw-polygon',
    fileExtension: '.svg',
    isPointer: false,
    getPreviewText: defaultPreviewText,
    shouldTruncatePreview: defaultShouldTruncatePreview,
    Component: SvgArtifactComponent,
  });

  registerArtifactType({
    type: 'application/vnd.mermaid',
    label: 'Mermaid',
    name: 'Mermaid Diagram',
    icon: 'chart-diagram',
    fileExtension: '.mmd',
    isPointer: false,
    getPreviewText: defaultPreviewText,
    shouldTruncatePreview: defaultShouldTruncatePreview,
    Component: MermaidArtifactComponent,
  });
}
